import type {
  RefundLedgerEntry,
  SavedPaymentAccountRefundSummary,
  TradingAccountRefundBalance,
} from "@/api/cashier";
import type { FlattenedRefundAccount } from "./helpers";

/**
 * Refund payment methods whose cards are rendered as pre-selected — one card per
 * approved saved payment account, plus one "add new" card — instead of using the
 * saved-account "Select" popover.
 *
 * Zota and Naewe are the placeholder-SPA payment methods: the BE narrows their
 * `refund_accounts` to the singleton placeholder + approved SPAs (PNX-5373), so
 * pending withdrawal SPAs no longer each spawn a duplicate refund card. Both are
 * listed here; the old saved-account popover machinery is removed because no
 * payout-eligible PM uses it any more.
 */
export const PRESELECTED_REFUND_PM_IDS = new Set<string>(["zotapay", "naewe"]);

/**
 * Pre-selected refund cards source their amount inputs from the matching
 * `refund_accounts` entries (which carry `ledger_entries`), but the single
 * "add new" card is keyed by the payment-method id (`${pmId}_${code}` →
 * `newRefundSavedPaymentMethods`). For Zota/Naewe there is no null-id
 * refund_account (the placeholder has a real id), so `refundAccsMap` has no
 * `${pmId}` entry — `calculateRefundAmountLeft` would silently drop the add-new
 * amount and `calcMaxRefunds` would produce no `maxRefunds[pmId]`.
 *
 * This adds one synthetic `${pmId}_${code}` entry per preselected PM, sourced
 * from that PM's `refund_accounts` ledger entries (the placeholder carries the
 * refundable ledger). `id` is set null so `calcMaxRefunds` keys the result by
 * `payment_method_id`, which is what the add-new card reads. Mirrors how the
 * credit-card null-id "add new" row already gets a `${payment_method_id}` key.
 */
export const addPreselectedAddNewRefundEntries = (
  refundAccsMap: { [k: string]: FlattenedRefundAccount },
  balance: TradingAccountRefundBalance | undefined,
  preselectedPmIds: ReadonlySet<string> = PRESELECTED_REFUND_PM_IDS
): { [k: string]: FlattenedRefundAccount } => {
  if (!balance?.payout_accounts) return refundAccsMap;

  const result = { ...refundAccsMap };

  for (const pmId of Object.keys(balance.payout_accounts)) {
    if (!preselectedPmIds.has(pmId)) continue;

    for (const account of balance.refund_accounts) {
      if (account.payment_method_id !== pmId) continue;

      const { ledger_entries, ...accountFields } = account;
      for (const entry of ledger_entries) {
        const key = `${pmId}_${entry.minimum_amount.code}`;
        if (result[key]) continue; // first ledger entry per currency wins
        result[key] = {
          ...accountFields,
          id: null,
          ledger_id: entry.ledger_id,
          minimum_amount: entry.minimum_amount,
          maximum_amount: entry.maximum_amount,
        };
      }
    }
  }

  return result;
};

/**
 * The add-new card for a preselected PM is keyed by `payment_method_id`, but the
 * sibling-ledger map (`refundMethodsByLedger`, used by `calcMaxRefunds` to reduce
 * each card's max by what siblings sharing the ledger have allocated) is built
 * only from `refund_accounts` ids. So the add-new card is not yet a sibling of
 * the approved cards on the shared ledger.
 *
 * This adds each preselected PM's `payment_method_id` to every ledger that PM
 * participates in, so the approved cards' maxima reduce as the add-new card is
 * allocated (and vice-versa). Mutates and returns the passed map (built fresh
 * per render).
 */
export const addPreselectedLedgerSiblings = (
  byLedger: Record<string, Set<string>>,
  refundAccounts: SavedPaymentAccountRefundSummary[],
  preselectedPmIds: ReadonlySet<string> = PRESELECTED_REFUND_PM_IDS
): Record<string, Set<string>> => {
  for (const account of refundAccounts) {
    if (!preselectedPmIds.has(account.payment_method_id)) continue;
    for (const entry of account.ledger_entries) {
      byLedger[entry.ledger_id] ??= new Set();
      byLedger[entry.ledger_id].add(account.payment_method_id);
    }
  }
  return byLedger;
};

export type PreselectedRefundView = {
  /** Preselected PMs present in `payout_accounts` (each renders pre-selected cards + one add-new card). */
  paymentMethodIds: string[];
  /** Approved SPA ids per PM (from payout_accounts.accounts) — the only `refund_accounts` rendered as pre-selected cards; excludes the placeholder and pending SPAs. */
  approvedSpaIdsByPm: Map<string, Set<string>>;
  /** Refundable ledger entries per PM (the placeholder carries the deposit ledger), deduped by currency — drives the single add-new card's amount inputs. */
  ledgerEntriesByPm: Map<string, RefundLedgerEntry[]>;
};

/**
 * Derives everything the refund step needs to render preselected PMs: which PMs
 * are preselected, their approved SPA id-set (so only approved cards render —
 * placeholder/pending are excluded), and the merged refundable ledger entries
 * for the add-new card.
 */
export const buildPreselectedRefundView = (
  balance: TradingAccountRefundBalance | undefined,
  preselectedPmIds: ReadonlySet<string> = PRESELECTED_REFUND_PM_IDS
): PreselectedRefundView => {
  const paymentMethodIds = Object.keys(balance?.payout_accounts ?? {}).filter(
    (pmId) => preselectedPmIds.has(pmId)
  );

  const approvedSpaIdsByPm = new Map<string, Set<string>>(
    paymentMethodIds.map((pmId) => [
      pmId,
      new Set(
        (balance?.payout_accounts?.[pmId]?.accounts ?? []).map((a) => a.id)
      ),
    ])
  );

  const ledgerEntriesByPm = new Map<string, RefundLedgerEntry[]>(
    paymentMethodIds.map((pmId) => {
      const seenCodes = new Set<string>();
      const entries: RefundLedgerEntry[] = [];
      for (const account of balance?.refund_accounts ?? []) {
        if (account.payment_method_id !== pmId) continue;
        for (const entry of account.ledger_entries) {
          if (seenCodes.has(entry.minimum_amount.code)) continue;
          seenCodes.add(entry.minimum_amount.code);
          entries.push(entry);
        }
      }
      return [pmId, entries];
    })
  );

  return { paymentMethodIds, approvedSpaIdsByPm, ledgerEntriesByPm };
};
